import { addDays } from "date-fns";
import { flatten } from "ramda";
import React from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as FowlIcon } from "../../../assets/icons/fowl.svg";
import i18n from "../../../i18n";
import PriceReport from "../../averagePrices/PriceReport/PriceReport";
import { createTableRow } from "../../averagePrices/PriceReport/Table";
import { useWeeklyPagination } from "../../averagePrices/PriceReport/useWeeklyPagination";
import { Poultry } from "../../averagePrices/types";
import Cookies from "js-cookie";

const mapProductToTableRow = (week, product, getName) => {
  const productData = week.products.find(p => p.product.id === product.id);

  return createTableRow({
    id: product.id,
    name: getName(product.id),
    currentPrice: productData ? productData.current_price : undefined,
    percentagePriceChange: productData
      ? productData.percentage_price_change
      : undefined,
  });
};

const PriceReportSection = ({
  weeklyData,
  selectedProducts,
  selectedRegions,
    title
}) => {
  const { t } = useTranslation(["averagePrices"]);
  const token = Cookies.get("user_token");

  const {
    getCurrentWeekData,
    isPrevWeekWithinRange,
    isNextWeekWithinRange,
    weekStartDatePrev,
    weekStartDate,
    weekStartDateNext,
    goBackOneWeek,
    goForwardOneWeek,
  } = useWeeklyPagination(weeklyData);

  const productList = (() => {
    if (!selectedRegions) {
      const currentWeekData = getCurrentWeekData();
      if (!currentWeekData) {
        return [];
      }

      return selectedProducts.map(product =>
        mapProductToTableRow(currentWeekData, product, id => t(Poultry[id]))
      );
    }

    const dataForProducts = selectedRegions.map(region => {
      const currentWeekData = getCurrentWeekData(
        week => week.region === region.id
      );
      if (!currentWeekData) {
        return [];
      }

      const mapProductToName = productId =>
        `${t(Poultry[productId])} ${
          i18n.language === "pl" ? region.shortcut : region.shortcut_en
        }`;

      return selectedProducts.map(product =>
        mapProductToTableRow(currentWeekData, product, mapProductToName)
      );
    });

    return flatten(dataForProducts);
  })();

  return (
    <PriceReport>
      <PriceReport.Header>
        <PriceReport.HeaderTitle>
          <FowlIcon style={{ marginTop: -10, marginRight: 10 }} />
          {t("averagePrices:report")} {title}
        </PriceReport.HeaderTitle>
      </PriceReport.Header>
      <PriceReport.TableSection>
        <PriceReport.Table
          productList={productList}
          header={
            <tr>
              <th>
                {isPrevWeekWithinRange && token && (
                  <PriceReport.PreviousWeekButton
                    onClick={goBackOneWeek}
                    weekStart={weekStartDatePrev}
                    weekEnd={addDays(weekStartDatePrev, 6)}
                  />
                )}
              </th>
              <th>
                <PriceReport.PeriodText
                  weekStart={weekStartDate}
                  weekEnd={addDays(weekStartDate, 6)}
                />
              </th>
              <th>
                {isNextWeekWithinRange && (
                  <PriceReport.NextWeekButton
                    onClick={goForwardOneWeek}
                    weekStart={weekStartDateNext}
                    weekEnd={addDays(weekStartDateNext, 6)}
                  />
                )}
              </th>
            </tr>
          }
        />
      </PriceReport.TableSection>
      <PriceReport.Source>{t("averagePrices:source")}</PriceReport.Source>
    </PriceReport>
  );
};

export default PriceReportSection;
